/**
 * @file Defines the FacetsTablet custom element.
 * @extends HTMLDivElement
 */
class FacetsTablet extends window.BAO.CustomElement({
  SuperClass: HTMLDivElement,
}) {
  constructor () {
    super()

    const source = window.theme.category || window.theme.searchResults
    this.state = {
      root: source ? source.stateLayer.state : undefined,
      facets: [],
    }

    this.eventBus = source ? source.eventBus : undefined

    if (!this.eventBus) {
      console.error('Event bus not available for FacetsTablet')
    }
    if (this.state.root === undefined) {
      console.error('State root not available for FacetsTablet')
    }

    this._FACET_KEYS = Object.freeze({
      PRICE: 'klevu_price',
      TYPE: 'type',
      GENDER: 'gender',
      BRAND: 'brand',
      TAGS: 'tags',
      CONTACT_US: 'contact_us',
      BACK_IN_STOCK: 'back_in_stock',
      COMING_SOON: 'coming_soon',
    })
  }

  /**
   * Standard custom element lifecycle callback.
   * Called when the element is inserted into the DOM.
   */
  connectedCallback () {
    this.subscribeToEvents()
  }

  /**
   * Subscribes to relevant events, primarily for facet data updates.
   */
  subscribeToEvents () {
    if (!this.eventBus) return

    let hasRendered = false

    this.eventBus.subscribe('api:facets:fetched', response => {
      this._processFetchedFacets(response)

      if (!hasRendered) {
        this._renderInitialFacets()
        hasRendered = true
      }
    })
  }

  /**
   * Processes the facet data received from the API.
   * @private
   * @param {Array<Object>} response - The array of facet objects from the API.
   */
  _processFetchedFacets (response) {
    this.state.facets = Array.isArray(response) ? response : []
  }

  /**
   * Adds a default 'price' facet if not present and sorts all facets
   * based on the configured filter order from `window.theme.collection.filterOrder`.
   * @private
   */
  _sortFacets () {
    if (window.theme.collection && window.theme.collection.filterOrder) {
      const filterOrder = window.theme.collection.filterOrder
        .split(',')
        .map(filter => filter.trim().toLowerCase())

      const getSortOrder = facet => {
        const index = filterOrder.indexOf(facet.label.toLowerCase())
        return index === -1 ? filterOrder.length : index
      }

      this.state.facets.sort((a, b) => {
        return getSortOrder(a) - getSortOrder(b)
      })
    }
  }

  /**
   * Renders the initial set of facets into the DOM.
   * This method is typically called once after the first batch of facet data is fetched.
   * @private
   */
  _renderInitialFacets () {
    const itemsContainer = this.querySelector(
      '.clc-ProductGridFacetsTablet_Items',
    )

    if (!itemsContainer) {
      console.error('Facet items container not found for rendering.')
      return
    }

    this.state.facets.forEach((facet, index) => {
      if (facet.key === this._FACET_KEYS.PRICE) {
        const html = this.buildPriceHTML(facet, index)
        if (html) {
          itemsContainer.insertAdjacentHTML('beforeend', html)
        }
      } else {
        const html = this.buildHTML(facet, index)
        if (html) {
          itemsContainer.insertAdjacentHTML('beforeend', html)
        }
      }
    })
  }

  buildPriceHTML (facet, index) {
    const existingFacetIndex = this.state.root.activeFacets.findIndex(
      activeFacet => activeFacet.key === 'klevu_price',
    )

    const currentMinFacet = this.state.root.activeFacets[existingFacetIndex]
      ?.values[0]
    const currentMaxFacet = this.state.root.activeFacets[existingFacetIndex]
      ?.values[1]

    const currentMinValue =
      currentMinFacet && Number(currentMinFacet) >= Number(facet.min)
        ? currentMinFacet
        : facet.min
    const minValue = facet.min

    const currentMaxValue =
      currentMaxFacet && Number(currentMaxFacet) <= Number(facet.max)
        ? currentMaxFacet
        : facet.max
    const maxValue = facet.max

    const startValue = facet.start === null ? minValue : facet.start

    const currencySymbol = window.theme.moneyFormat.split('{')[0]

    return `
    <div class="clc-ProductGridFacetsTablet_Item clc-ProductGridFacetsTablet_Item-children">
      <details
        class="clc-ProductGridFacetTablet"
        ${this.filtersClosedByDefault(facet) ? '' : 'open'}
        data-fully-open="false"
        is="facet-summary"
        data-label="${this.handleize(facet.label)}"
        style="order: ${index};"
      >
        <summary class="clc-ProductGridFacetTablet_Summary fz-15_35" data-facet-summary-el="summary">
          Price

          <svg class="clc-ProductGridFacetTablet_FilterLabelIcon" xmlns="http://www.w3.org/2000/svg">
            <use href="#icon-chevron-down"></use>
          </svg>
        </summary>

        <div class="clc-ProductGridFacetTablet_Display" data-facet-summary-el="content">
          <div data-filter-facet-search-external-el="body" data-range-slider-external-el="container">
              <price-range class="prc-Range">
                <div class="prc-Range_Column">
                  <div class="prc-Range_Field">
                    <input class="prc-Range_Input" id="price-range-min" type="number" value="${startValue}" data-range-slider-external-el="min" placeholder="${currencySymbol}${currentMinValue}" min="${minValue}" max="${maxValue}" data-price-range-el="min" />

                    <label class="prc-Range_Label" for="price-range-min" data-currency-symbol="${currencySymbol}" data-text="${currencySymbol}${currentMinValue}">
                      <span class="util-ScreenReaderOnly">
                        {{ 'sections.collection_template.from' | t }}:
                        {{ cart.currency.symbol }}${currentMinValue}</span>
                    </label>
                  </div>
                </div>

                <div class="prc-Range_Column">
                  <div class="prc-Range_Field">
                    <input class="prc-Range_Input" id="price-range-max" type="number" data-range-slider-external-el="max" placeholder="${currencySymbol}${currentMaxValue}" min="${minValue}" max="${maxValue}" data-price-range-el="max" />

                    <label class="prc-Range_Label" for="price-range-max" data-currency-symbol="${currencySymbol}" data-text="${currencySymbol}${currentMaxValue}">
                      <span class="util-ScreenReaderOnly">
                        {{ 'sections.collection_template.to' | t }}:
                        {{ cart.currency.symbol }}${currentMaxValue}</span>
                    </label>
                  </div>
                </div>
              </price-range>

              <div class="clc-ProductGridFacetTablet_RangeSlider">
                <range-slider
                  class="rng-Slider"
                  min="${minValue}"
                  max="${maxValue}"
                  min-value="${currentMinValue}"
                  max-value="${currentMaxValue}">
                  <div class="rng-Slider_Inner">
                    <button
                      class="rng-Slider_Dot rng-Slider_Dot-left"
                      type="button"
                      data-range-slider-el="touchLeft">
                      <span class="rng-Slider_DotInner"></span>
                    </button>

                    <button
                      class="rng-Slider_Dot rng-Slider_Dot-right"
                      type="button"
                      data-range-slider-el="touchRight"
                      style="right: 0%;">
                      <span class="rng-Slider_DotInner"></span>
                    </button>

                    <div class="rng-Slider_Line" data-range-slider-el="line">
                      <span class="rng-Slider_LineInner"></span>
                      <span class="rng-Slider_LineHighlighter" data-range-slider-el="highlighter"></span>
                    </div>
                  </div>
                </range-slider>
              </div>
          </div>
      </details>
    </div>
    `
  }

  /**
   * Builds the HTML string for a single facet.
   * @param {Object} facet - The facet object to build HTML for.
   * @param {number} index - The index of the facet, used for ordering.
   * @returns {string} The HTML string for the facet, or an empty string if the facet should not be rendered.
   */
  buildHTML (facet, index) {
    if (facet.key === this._FACET_KEYS.TYPE && !this.showProductTypeFilter) {
      return ''
    }

    if (facet.key === this._FACET_KEYS.PRICE && !this.showPriceFilter) {
      return ''
    }

    if (facet.key === this._FACET_KEYS.GENDER && !this.showGenderFilter) {
      return ''
    }

    if (
      facet.key === this._FACET_KEYS.TAGS ||
      facet.key === this._FACET_KEYS.CONTACT_US ||
      facet.key === this._FACET_KEYS.BACK_IN_STOCK ||
      facet.key === this._FACET_KEYS.COMING_SOON
    ) {
      return ''
    }

    if (this.isMakerCollection && facet.key === this._FACET_KEYS.BRAND) {
      return ''
    }

    return `
    <div class="clc-ProductGridFacetsTablet_Item clc-ProductGridFacetsTablet_Item-children">
      <details
        class="clc-ProductGridFacetTablet"
        ${this.filtersClosedByDefault(facet) ? '' : 'open'}
        data-fully-open="false"
        is="facet-summary"
        data-label="${this.handleize(facet.label)}"
        style="order: ${index};"
      >
      <summary class="clc-ProductGridFacetTablet_Summary fz-15_35" data-facet-summary-el="summary">
        ${facet.label}

        <svg class="clc-ProductGridFacetTablet_FilterLabelIcon" xmlns="http://www.w3.org/2000/svg">
          <use href="#icon-chevron-down"></use>
        </svg>
      </summary>

      <div class="clc-ProductGridFacetTablet_Display" data-facet-summary-el="content">
        <div class="clc-ProductGridFacetTablet_Body" data-filter-facet-search-external-el="body">
        ${
          this.filtersWithSearch.includes(facet.label.toLowerCase())
            ? `
        <div class="clc-ProductGridFacetTablet_Search">
          <input class="clc-ProductGridFacetTablet_SearchInput"
            is="filter-facet-search"
            value=""
            placeholder="Search ${facet.label}"
            data-filter-facet-el="search"
          >
          </div>
          `
            : ''
        }
        <ul class="clc-ProductGridFacetTablet_List">
          ${facet.options
            .map(option => {
              return `
              <li class="clc-ProductGridFacetTablet_Item" data-filter-facet-search-external-el="item">
                <label class="clc-ProductGridFacetTablet_Label fz-15_24" data-filter-facet-search-external-el="label" is="facet-checkbox" data-facet="${
                  facet.key
                }" data-option="${option.name}" ${
                option.count === 0
                  ? 'aria-disabled="true"'
                  : 'aria-disabled="false"'
              }>
                <input is="facet-checkbox" data-facet="${
                  facet.key
                }" class="clc-ProductGridFacetTablet_Checkbox" type="checkbox" name="${
                option.name
              }" value="${option.value}" ${option.selected ? 'checked' : ''}>
                <span class="clc-ProductGridFacetTablet_CheckboxIcon"></span>
                <span class="clc-ProductGridFacetTablet_CheckboxText">
                  ${option.name}
                </span>
                </label>

              </li>
            `
            })
            .join('')}
        </ul>
        </div>

      </div>
    </details>
    </div>`
  }

  /**
   * Gets whether the product type filter should be shown.
   * @returns {boolean} True if the product type filter should be shown, false otherwise.
   */
  get showProductTypeFilter () {
    return this.getAttribute('data-product-type-metafield') === 'true'
  }

  /**
   * Gets whether the gender filter should be shown.
   * @returns {boolean} True if the gender filter should be shown, false otherwise.
   */
  get showGenderFilter () {
    return this.getAttribute('data-gender-metafield') === 'true'
  }

  /**
   * Gets the list of filters that should have a search input.
   * The value is expected to be a comma-separated string from the attribute.
   * @returns {string | null} A string containing filter labels for search, or null if attribute is not set.
   */
  get filtersWithSearch () {
    return this.getAttribute('data-filters-with-search')
  }

  /**
   * Gets the configured filter order from the theme settings.
   * @returns {string | undefined} The filter order string, or undefined if not set.
   */
  get filterOrder () {
    return window.theme.collection.filterOrder
  }

  /**
   * Gets whether the current collection is a "maker" collection.
   * @returns {boolean} True if it's a maker collection, false otherwise.
   */
  get isMakerCollection () {
    return this.getAttribute('data-is-maker-collection') === 'true'
  }

  /**
   * Gets the price filter range element.
   * @returns {Element | null} The price filter DOM element, or null if not found.
   */
  get priceFilter () {
    return this.querySelector('.clc-ProductGridFacetTablet-priceRange')
  }

  /**
   * Determines if a facet should be closed by default.
   * @param {Object} facet - The facet object to check.
   * @returns {boolean} True if the facet should be closed by default, false otherwise.
   */
  filtersClosedByDefault (facet) {
    const filtersToBeClosedByDefault =
      window.theme.collection.filtersClosedByDefault

    if (this.state.facets.some(f => f.key === this._FACET_KEYS.GENDER)) {
      return true
    }

    if (filtersToBeClosedByDefault.includes(facet.label)) {
      return true
    }

    return false
  }

  handleize (str) {
    return str
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }
}

window.customElements.define('facets-tablet', FacetsTablet, {
  extends: 'div',
})
